/**
 * Mappers for System Audit
 *
 * Transforms between:
 *   IntegrityReport / IntegrityFinding  (UI - camelCase)
 *   SystemAuditRunRow                   (DB - snake_case)
 *   SystemAuditActionRow                (DB - snake_case)
 *   AuditRunSummary / AuditAction       (UI - camelCase)
 */

import type { IntegrityReport, IntegrityFinding } from "@/data/systemIntegrity";
import type {
  SystemAuditRunRow,
  SystemAuditActionRow,
  AuditRunSummary,
  AuditAction,
} from "@/services/system-audit.service";

// ── IntegrityReport: UI → DB ──────────────────────────────────────────────────

export function toDbAuditRun(
  report: IntegrityReport,
): Omit<SystemAuditRunRow, "id" | "created_at"> {
  return {
    ran_at: report.runAt,
    total: report.summary.total,
    critical: report.summary.critical,
    warning: report.summary.warning,
    info: report.summary.info,
    checks_run: report.summary.checksRun,
    passed: report.summary.passed,
  };
}

// ── SystemAuditRunRow: DB → UI ────────────────────────────────────────────────

export function toUiAuditRun(row: SystemAuditRunRow): AuditRunSummary {
  return {
    id: row.id,
    ranAt: row.ran_at,
    total: row.total,
    critical: row.critical,
    warning: row.warning,
    info: row.info,
    checksRun: row.checks_run,
    passed: row.passed,
  };
}

// ── IntegrityFinding + action: UI → DB ───────────────────────────────────────

export function toDbAuditAction(
  finding: IntegrityFinding,
  actionType: "applied" | "dismissed",
  performedBy?: string,
  note?: string,
): Omit<SystemAuditActionRow, "id" | "created_at"> {
  return {
    finding_id: finding.id,
    action_type: actionType,
    performed_by: performedBy ?? null,
    note: note ?? null,
  };
}

// ── SystemAuditActionRow: DB → UI ─────────────────────────────────────────────

export function toUiAuditAction(row: SystemAuditActionRow): AuditAction {
  return {
    id: row.id,
    findingId: row.finding_id,
    actionType: row.action_type,
    performedBy: row.performed_by,
    note: row.note,
    createdAt: row.created_at,
  };
}
